"use client";

import { c as _c } from "react/compiler-runtime";
import { IconArrowLeftRight } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconChevronDownOutline18, IconGripDotsVerticalOutline18, IconPlusOutline18, IconTrashOutline18 } from "nucleo-ui-outline-18";
import { SearchableWalletSelect } from "@/app/(v2)/_components/SearchableWalletSelect";
import type { TurnkeyWallet } from "@/app/(v2)/_types/wallet";
import { addressEquals } from "@/lib/utils/address";
import TokenSelector from "@/app/(v2)/workflows/_components/form-fields/TokenSelector";
import { useChainInfoMap } from "@/app/(v2)/workflows/_hooks/useChainInfo";
import { Input } from "@/components/ui/input";
import { AutoSwapBalancesPreviewSkeleton, AutoSwapConfigSkeleton } from "./_components/SettingsSkeletons";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { TokenIcon } from "@/components/ui/token-icon";
import { TokenWithChainIcon } from "@/components/ui/token-with-chain-icon";
import { useAggregatedWalletBalances } from "@/hooks/useAggregatedWalletBalances";
import { AUTO_SWAP_MIN_ELIGIBLE_USD, getAutoSwapCandidateWallets } from "@/hooks/useAutoSwapCoverage";
import { useAutoSwapSettings, type AutoSwapMode, type OrgAutoSwapSource } from "@/hooks/useAutoSwapSettings";
import { useOrganizationWallets } from "@/hooks/useOrganizationWallets";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useSimTokenInfo, type SimBalanceItem } from "@/hooks/useSimTokenInfo";
import { getChainInfo } from "@/lib/chains";
import { toast } from "@/lib/toast";
import { cn, formatAddress, formatUsdValue } from "@/lib/utils";
import type { OrganizationWallet } from "@/types/wallet";
import { DndContext, DragOverlay, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent, type DragStartEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";

// ─── Draft state ──────────────────────────────────────────────────────────────

interface SourceDraft {
  /** Stable key for dnd-kit */
  key: string;
  id?: string;
  walletId: string;
  tokenAddress: string;
  chainId: number | undefined;
  /** Token decimals stored for UI display only (auto-detected from token metadata). */
  tokenDecimals: number;
  /** Stored as string for the number input; converted to number on save. */
  slippageBps: string;
}
function emptyDraft(): SourceDraft {
  return {
    key: crypto.randomUUID(),
    walletId: "",
    tokenAddress: "",
    chainId: undefined,
    tokenDecimals: 18,
    slippageBps: "300"
  };
}
function draftFromSource(src: OrgAutoSwapSource): SourceDraft {
  return {
    key: crypto.randomUUID(),
    id: src.id,
    walletId: src.walletId,
    tokenAddress: src.tokenAddress,
    chainId: src.chainId,
    tokenDecimals: src.tokenDecimals ?? 18,
    slippageBps: String(src.slippageBps)
  };
}

// ─── Collapsed summary ────────────────────────────────────────────────────────

function SourceSummary(t0) {
  const $ = _c(23);
  const {
    draft,
    wallets,
    isAutoMode
  } = t0;
  let t1;
  if ($[0] !== draft.walletId || $[1] !== wallets) {
    t1 = wallets.find(w => w.walletId === draft.walletId)?.walletName;
    $[0] = draft.walletId;
    $[1] = wallets;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const walletName = t1;
  const {
    data: tokenInfo
  } = useSimTokenInfo(draft.tokenAddress || undefined, draft.chainId, Boolean(!isAutoMode && draft.tokenAddress && draft.chainId));
  const tokenMeta = tokenInfo?.tokens?.[0];
  const symbol = tokenMeta?.symbol ?? (draft.tokenAddress ? draft.tokenAddress.slice(0, 6) + "\u2026" : null);
  if (isAutoMode) {
    if (!walletName) {
      let t2;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = <span className="body-sm text-ds-text-disabled">Not configured</span>;
        $[3] = t2;
      } else {
        t2 = $[3];
      }
      return t2;
    }
    let t2;
    if ($[4] !== walletName) {
      t2 = <span className="font-medium">{walletName}</span>;
      $[4] = walletName;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    let t3;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <span className="text-ds-text-disabled dark:text-ds-text-muted">· all tokens, auto-selected</span>;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] !== t2) {
      t4 = <div className="flex items-center gap-2 text-sm">{t2}{t3}</div>;
      $[7] = t2;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  if (!walletName && !symbol && !draft.chainId) {
    let t2;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <span className="body-sm text-ds-text-disabled">Not configured</span>;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    return t2;
  }
  let t2;
  if ($[10] !== draft.chainId || $[11] !== draft.tokenAddress || $[12] !== symbol || $[13] !== tokenMeta) {
    t2 = symbol && <span className="flex items-center gap-1.5 font-medium">{tokenMeta && <TokenIcon symbol={tokenMeta.symbol} logoUrl={tokenMeta.logo ?? tokenMeta.token_metadata?.logo} tokenAddress={draft.tokenAddress} chainId={draft.chainId} size={16} />}{symbol}</span>;
    $[10] = draft.chainId;
    $[11] = draft.tokenAddress;
    $[12] = symbol;
    $[13] = tokenMeta;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  let t3;
  if ($[15] !== draft.chainId) {
    t3 = draft.chainId && <span className="text-ds-text-disabled">on {getChainInfo(draft.chainId).name}</span>;
    $[15] = draft.chainId;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== walletName) {
    t4 = walletName && <span className="text-ds-text-disabled">from {walletName}</span>;
    $[17] = walletName;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== t2 || $[20] !== t3 || $[21] !== t4) {
    t5 = <div className="flex items-center gap-2 text-sm">{t2}{t3}{t4}</div>;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  return t5;
}
const AUTO_SWAP_TOKENS = [{
  symbol: "ETH",
  chainId: 1
}, {
  symbol: "POL",
  chainId: 137
}, {
  symbol: "USDC",
  chainId: 8453
}, {
  symbol: "WBTC",
  chainId: 1
}, {
  symbol: "SOL",
  chainId: 0
}, {
  symbol: "ARB",
  chainId: 42161
}] as const;
function AutoSwapTokenAnimation() {
  const $ = _c(42);
  const reduceMotion = useReducedMotion();
  const [currentIndex, setCurrentIndex] = useState(0);
  let t0;
  let t1;
  if ($[0] !== reduceMotion) {
    t0 = () => {
      if (reduceMotion) {
        return;
      }
      const timer = setInterval(() => {
        setCurrentIndex(_temp);
      }, 1800);
      return () => clearInterval(timer);
    };
    t1 = [reduceMotion];
    $[0] = reduceMotion;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  const left = AUTO_SWAP_TOKENS[currentIndex];
  const right = AUTO_SWAP_TOKENS[(currentIndex + 2) % AUTO_SWAP_TOKENS.length];
  let t3;
  if ($[3] !== left || $[4] !== right) {
    t3 = [left, right];
    $[3] = left;
    $[4] = right;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  t2 = t3;
  const [leftToken, rightToken] = t2;
  let t4;
  if ($[6] !== reduceMotion) {
    t4 = reduceMotion ? {
      initial: {
        opacity: 1
      },
      animate: {
        opacity: 1
      },
      exit: {
        opacity: 1
      }
    } : {
      initial: {
        opacity: 0,
        y: 10,
        scale: 0.78,
        filter: "blur(4px)"
      },
      animate: {
        opacity: 1,
        y: 0,
        scale: 1,
        filter: "blur(0px)"
      },
      exit: {
        opacity: 0,
        y: -10,
        scale: 0.78,
        filter: "blur(4px)"
      }
    };
    $[6] = reduceMotion;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const iconVariants = t4;
  const t5 = `left-${leftToken.symbol}-${currentIndex}`;
  const t6 = reduceMotion ? 0 : 0.28;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [0.2, 0, 0, 1];
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t6) {
    t8 = {
      duration: t6,
      ease: t7
    };
    $[9] = t6;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== leftToken.chainId || $[12] !== leftToken.symbol) {
    t9 = <TokenIcon symbol={leftToken.symbol} chainId={leftToken.chainId} size={32} />;
    $[11] = leftToken.chainId;
    $[12] = leftToken.symbol;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== iconVariants || $[15] !== t5 || $[16] !== t8 || $[17] !== t9) {
    t10 = <AnimatePresence mode="popLayout" initial={false}><motion.div key={t5} initial="initial" animate="animate" exit="exit" variants={iconVariants} transition={t8} className="flex">{t9}</motion.div></AnimatePresence>;
    $[14] = iconVariants;
    $[15] = t5;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== reduceMotion) {
    t11 = reduceMotion ? undefined : {
      rotate: [0, 180]
    };
    $[19] = reduceMotion;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== reduceMotion) {
    t12 = reduceMotion ? undefined : {
      duration: 0.9,
      ease: [0.22, 1, 0.36, 1],
      repeat: Infinity,
      repeatDelay: 0.9
    };
    $[21] = reduceMotion;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <IconArrowLeftRight className="h-4 w-4" />;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t11 || $[25] !== t12) {
    t14 = <motion.div animate={t11} transition={t12} className="text-ds-text-disabled dark:text-ds-text-muted">{t13}</motion.div>;
    $[24] = t11;
    $[25] = t12;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const t15 = `right-${rightToken.symbol}-${currentIndex}`;
  const t16 = reduceMotion ? 0 : 0.28;
  let t17;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = [0.2, 0, 0, 1];
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== t16) {
    t18 = {
      duration: t16,
      ease: t17
    };
    $[28] = t16;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== rightToken.chainId || $[31] !== rightToken.symbol) {
    t19 = <TokenIcon symbol={rightToken.symbol} chainId={rightToken.chainId} size={32} />;
    $[30] = rightToken.chainId;
    $[31] = rightToken.symbol;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== iconVariants || $[34] !== t15 || $[35] !== t18 || $[36] !== t19) {
    t20 = <AnimatePresence mode="popLayout" initial={false}><motion.div key={t15} initial="initial" animate="animate" exit="exit" variants={iconVariants} transition={t18} className="flex">{t19}</motion.div></AnimatePresence>;
    $[33] = iconVariants;
    $[34] = t15;
    $[35] = t18;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t10 || $[39] !== t14 || $[40] !== t20) {
    t21 = <div className="mt-5 inline-flex items-center gap-3">{t10}{t14}{t20}</div>;
    $[38] = t10;
    $[39] = t14;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  return t21;
}
function _temp(index) {
  return (index + 1) % AUTO_SWAP_TOKENS.length;
}
function AutoSwapModePreviewCard(t0) {
  const $ = _c(27);
  const {
    mode,
    wallets,
    walletBalances,
    isLoading
  } = t0;
  let t1;
  if ($[0] !== walletBalances || $[1] !== wallets) {
    let t2;
    if ($[3] !== walletBalances) {
      t2 = wallet => wallet.walletId ? (walletBalances.get(wallet.walletId) ?? []).map(_temp2) : [];
      $[3] = walletBalances;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = new Set(wallets.flatMap(t2));
    $[0] = walletBalances;
    $[1] = wallets;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = [...t1];
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const {
    chainInfoMap
  } = useChainInfoMap(t2);
  let t3;
  let t4;
  let t5;
  if ($[7] !== chainInfoMap || $[8] !== isLoading || $[9] !== mode || $[10] !== walletBalances || $[11] !== wallets) {
    let t6;
    if ($[15] !== walletBalances) {
      t6 = wallet_0 => {
        const walletId = wallet_0.walletId;
        if (!walletId) {
          return [];
        }
        return (walletBalances.get(walletId) ?? []).map(balance_0 => ({
          balance: balance_0,
          wallet: wallet_0
        }));
      };
      $[15] = walletBalances;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    let t7;
    if ($[17] !== mode) {
      t7 = item => {
        const valueUsd = item.balance.value_usd ?? 0;
        if (valueUsd <= 0) {
          return false;
        }
        if (mode === "auto_lowest_balance" && valueUsd < AUTO_SWAP_MIN_ELIGIBLE_USD) {
          return false;
        }
        return true;
      };
      $[17] = mode;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    let t8;
    if ($[19] !== mode) {
      t8 = (a, b) => mode === "auto_lowest_balance" ? (a.balance.value_usd ?? 0) - (b.balance.value_usd ?? 0) : (b.balance.value_usd ?? 0) - (a.balance.value_usd ?? 0);
      $[19] = mode;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    const orderedBalances = wallets.flatMap(t6).filter(t7).sort(t8);
    const previewBalances = orderedBalances.slice(0, 5);
    t5 = "shadow-ds-soft border-ds-border-default bg-ds-surface-card rounded-2xl border p-3";
    t3 = "rounded-xl";
    t4 = isLoading ? <AutoSwapBalancesPreviewSkeleton /> : previewBalances.length > 0 ? <div className="divide-ds-neutral-200 dark:divide-ds-neutral-800 divide-y">{previewBalances.map(t9 => {
        const {
          balance: balance_1,
          wallet: wallet_1
        } = t9;
        const chainInfo = chainInfoMap.get(balance_1.chain_id) ?? getChainInfo(balance_1.chain_id);
        const chainIconUrl = "iconUrl" in chainInfo ? chainInfo.iconUrl : null;
        const formattedUsd = formatUsdValue(balance_1.value_usd) ?? (balance_1.value_usd && balance_1.value_usd > 0 ? "<$0.01" : "$0.00");
        return <div key={`${wallet_1.walletId}:${balance_1.chain_id}:${balance_1.address}`} className="flex items-center gap-3 py-3 first:pb-3 first:pt-1 last:pb-1"><TokenWithChainIcon symbol={balance_1.symbol} logoUrl={balance_1.token_metadata?.logo} tokenAddress={balance_1.address} chainId={balance_1.chain_id} tokenSize={28} chainIconUrl={chainIconUrl} chainName={chainInfo.name} chainIconSize={14} chainClassName="border dark:border-zinc-950" /><div className="min-w-0 flex-1"><div className="flex items-center gap-2"><p className="body-sm-semibold truncate">{balance_1.symbol}</p><span className="text-ds-text-disabled text-xs">{chainInfo.name}</span></div><p className="text-ds-text-disabled truncate text-xs">from {wallet_1.walletName ?? "Wallet"} ({formatAddress(wallet_1.address, 2, 2)})</p></div><div className="text-right"><p className="body-sm-semibold tabular-nums">{formattedUsd}</p></div></div>;
      })}</div> : <div className="text-ds-text-muted px-2 py-6 text-sm">No eligible tokens found across your org wallets.</div>;
    $[7] = chainInfoMap;
    $[8] = isLoading;
    $[9] = mode;
    $[10] = walletBalances;
    $[11] = wallets;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t3 = $[12];
    t4 = $[13];
    t5 = $[14];
  }
  let t6;
  if ($[21] !== t3 || $[22] !== t4) {
    t6 = <div className={t3}>{t4}</div>;
    $[21] = t3;
    $[22] = t4;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== t5 || $[25] !== t6) {
    t7 = <div className={t5}>{t6}</div>;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  return t7;
}

// ─── Source row (sortable + collapsible) ──────────────────────────────────────
function _temp2(balance) {
  return balance.chain_id;
}
interface SourceRowProps {
  draft: SourceDraft;
  wallets: TurnkeyWallet[];
  index: number;
  onChange: (draft: SourceDraft) => void;
  onRemove: () => void;
  /** When true renders a lightweight placeholder during drag */
  isDragOverlay?: boolean;
  /** When true the drag handle is hidden (auto mode — order is determined by live balance) */
  isAutoMode?: boolean;
}
function SourceRowContent(t0) {
  const $ = _c(59);
  const {
    draft,
    wallets,
    index,
    onChange,
    onRemove,
    dragHandleProps,
    isDragging,
    isAutoMode
  } = t0;
  const pendingTokenRef = useRef(null);
  let t1;
  if ($[0] !== draft.walletId || $[1] !== wallets) {
    t1 = wallets.find(w => w.walletId === draft.walletId)?.address;
    $[0] = draft.walletId;
    $[1] = wallets;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const selectedWalletAddress = t1;
  const {
    data: tokenInfo
  } = useSimTokenInfo(draft.tokenAddress || undefined, draft.chainId, Boolean(!isAutoMode && draft.tokenAddress && draft.chainId));
  const tokenMeta = tokenInfo?.tokens?.[0];
  let t2;
  if ($[3] !== draft || $[4] !== onChange) {
    t2 = field => e => onChange({
      ...draft,
      [field]: e.target.value
    });
    $[3] = draft;
    $[4] = onChange;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const set = t2;
  let t3;
  let t4;
  if ($[6] !== draft || $[7] !== onChange || $[8] !== tokenMeta) {
    t3 = () => {
      if (!tokenMeta || tokenMeta.decimals === draft.tokenDecimals) {
        return;
      }
      onChange({
        ...draft,
        tokenDecimals: tokenMeta.decimals
      });
    };
    t4 = [draft, onChange, tokenMeta];
    $[6] = draft;
    $[7] = onChange;
    $[8] = tokenMeta;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  useEffect(t3, t4);
  const t5 = `bg-ds-surface-card rounded-xl border transition-opacity ${isDragging ? "opacity-40" : "border-ds-border-default"}`;
  let t6;
  if ($[11] !== dragHandleProps || $[12] !== isAutoMode) {
    t6 = !isAutoMode && <span {...dragHandleProps} className="hover:text-ds-text-muted dark:text-ds-text-subtle dark:hover:text-ds-text-disabled cursor-grab touch-none text-zinc-300 active:cursor-grabbing" title="Drag to reorder"><IconGripDotsVerticalOutline18 className="h-4 w-4" /></span>;
    $[11] = dragHandleProps;
    $[12] = isAutoMode;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = index + 1;
  let t8;
  if ($[14] !== t7) {
    t8 = <span className="bg-ds-neutral-100 text-ds-text-muted dark:bg-ds-neutral-800 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold">{t7}</span>;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = index + 1;
  let t10;
  if ($[16] !== t9) {
    t10 = <p className="body-sm-semibold">Source {t9}</p>;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t6 || $[20] !== t8) {
    t11 = <div className="flex items-center gap-2">{t6}{t8}{t10}</div>;
    $[18] = t10;
    $[19] = t6;
    $[20] = t8;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <IconTrashOutline18 className="h-3.5 w-3.5" />;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== onRemove) {
    t13 = <button type="button" onClick={onRemove} className="dark:text-ds-text-subtle shrink-0 rounded p-1 text-zinc-300 transition-colors hover:text-red-500 dark:hover:text-red-400" title="Remove">{t12}</button>;
    $[23] = onRemove;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t11 || $[26] !== t13) {
    t14 = <div className="border-ds-neutral-100 dark:border-ds-neutral-800 flex items-center justify-between gap-3 border-b px-4 py-3">{t11}{t13}</div>;
    $[25] = t11;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const t15 = isAutoMode ? "sm:col-span-2" : "";
  let t16;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <label className="field-label mb-1.5 block">Wallet</label>;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== draft || $[30] !== onChange || $[31] !== wallets) {
    t17 = address => {
      const wallet = wallets.find(w_0 => addressEquals(w_0.address, address));
      onChange({
        ...draft,
        walletId: wallet?.walletId ?? ""
      });
    };
    $[29] = draft;
    $[30] = onChange;
    $[31] = wallets;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== selectedWalletAddress || $[34] !== t17 || $[35] !== wallets) {
    t18 = <SearchableWalletSelect wallets={wallets} selectedAddress={selectedWalletAddress} onSelect={t17} variant="compact" />;
    $[33] = selectedWalletAddress;
    $[34] = t17;
    $[35] = wallets;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== isAutoMode) {
    t19 = isAutoMode && <p className="text-ds-text-disabled dark:text-ds-text-muted mt-1.5 text-xs">All tokens in this wallet are eligible — the system picks the best one at swap time.</p>;
    $[37] = isAutoMode;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t15 || $[40] !== t18 || $[41] !== t19) {
    t20 = <div className={t15}>{t16}{t18}{t19}</div>;
    $[39] = t15;
    $[40] = t18;
    $[41] = t19;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== draft.slippageBps || $[44] !== isAutoMode || $[45] !== set) {
    t21 = !isAutoMode && <div><label className="field-label mb-1.5 block">Slippage</label><div className="relative"><Input type="number" value={draft.slippageBps} onChange={set("slippageBps")} min={0} max={8000} placeholder="300" className="pr-16" /><span className="text-ds-text-disabled pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs">{draft.slippageBps ? `= ${(Number(draft.slippageBps) / 100).toFixed(1)}%` : "bps"}</span></div><p className="text-ds-text-disabled mt-1 text-xs">Default: 300 bps (3%).</p></div>;
    $[43] = draft.slippageBps;
    $[44] = isAutoMode;
    $[45] = set;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== draft || $[48] !== isAutoMode || $[49] !== onChange) {
    t22 = !isAutoMode && <div className="sm:col-span-2"><TokenSelector label="Token to sell" value={draft.tokenAddress} onChange={v => {
        const tokenAddress = String(v);
        pendingTokenRef.current = tokenAddress;
        onChange({
          ...draft,
          tokenAddress
        });
      }} chainId={draft.chainId} onChainIdChange={chainId => {
        const tokenAddress_0 = pendingTokenRef.current ?? draft.tokenAddress;
        pendingTokenRef.current = null;
        onChange({
          ...draft,
          chainId,
          tokenAddress: tokenAddress_0
        });
      }} showChainFilter={true} popoverModal={true} /></div>;
    $[47] = draft;
    $[48] = isAutoMode;
    $[49] = onChange;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== t20 || $[52] !== t21 || $[53] !== t22) {
    t23 = <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-[minmax(0,1fr)_180px]">{t20}{t21}{t22}</div>;
    $[51] = t20;
    $[52] = t21;
    $[53] = t22;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== t14 || $[56] !== t23 || $[57] !== t5) {
    t24 = <div className={t5}>{t14}{t23}</div>;
    $[55] = t14;
    $[56] = t23;
    $[57] = t5;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  return t24;
}
function SortableSourceRow(props) {
  const $ = _c(19);
  let t0;
  if ($[0] !== props.draft.key || $[1] !== props.isAutoMode) {
    t0 = {
      id: props.draft.key,
      disabled: props.isAutoMode
    };
    $[0] = props.draft.key;
    $[1] = props.isAutoMode;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable(t0);
  let t1;
  if ($[3] !== transform) {
    t1 = CSS.Transform.toString(transform);
    $[3] = transform;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1 || $[6] !== transition) {
    t2 = {
      transform: t1,
      transition
    };
    $[5] = t1;
    $[6] = transition;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const style = t2;
  let t3;
  if ($[8] !== attributes || $[9] !== listeners) {
    t3 = {
      ...attributes,
      ...listeners
    };
    $[8] = attributes;
    $[9] = listeners;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== isDragging || $[12] !== props || $[13] !== t3) {
    t4 = <SourceRowContent {...props} dragHandleProps={t3} isDragging={isDragging} />;
    $[11] = isDragging;
    $[12] = props;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== setNodeRef || $[16] !== style || $[17] !== t4) {
    t5 = <div ref={setNodeRef} style={style}>{t4}</div>;
    $[15] = setNodeRef;
    $[16] = style;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
}

// ─── Tab ──────────────────────────────────────────────────────────────────────

export function AutoSwapSettingsPanel({
  isOwner = true,
  variant = "page",
  onSaved,
  onEnabledChange,
  onDirtyChange
}: {
  isOwner?: boolean;
  variant?: "page" | "modal";
  onSaved?: () => void;
  onEnabledChange?: (enabled: boolean) => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const {
    selectedOrgId
  } = useOrganizations();
  const {
    settings,
    loading,
    saving,
    save
  } = useAutoSwapSettings();
  const {
    wallets,
    isLoading: walletsLoading
  } = useOrganizationWallets(selectedOrgId ?? undefined);
  const [enabled, setEnabled] = useState(false); // disabled by default
  const [mode, setMode] = useState<AutoSwapMode>("auto_lowest_balance");
  const [maxSwapAmountUsd, setMaxSwapAmountUsd] = useState("3");
  const [sources, setSources] = useState<SourceDraft[]>([]);
  const [dirty, setDirty] = useState(false);
  const [activeDragKey, setActiveDragKey] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const isAutoMode = mode !== "manual";

  // When mode changes, preserve all source drafts so users can switch back
  // without losing their configuration.
  const handleModeChange = (newMode: AutoSwapMode) => {
    setMode(newMode);
    if (newMode === "manual" && sources.length === 0) {
      setSources([emptyDraft()]);
    }
    setDirty(true);
  };
  const handleEnabledChange = (nextEnabled: boolean) => {
    setEnabled(nextEnabled);
    onEnabledChange?.(nextEnabled);
    setDirty(true);
  };
  const dndId = useId();
  useEffect(() => {
    if (!settings) return;
    setEnabled(settings.enabled);
    onEnabledChange?.(settings.enabled);
    setMode(settings.mode ?? "manual");
    setMaxSwapAmountUsd(settings.maxSwapAmountUsd != null ? String(settings.maxSwapAmountUsd) : "");
    setSources((settings.sources ?? []).map(draftFromSource));
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onEnabledChange is a callback prop, only re-run when settings change
  }, [settings]);
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // dnd-kit sensors
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates
  }));
  const handleDragStart = ({
    active
  }: DragStartEvent) => {
    setActiveDragKey(active.id as string);
  };
  const handleDragEnd = ({
    active,
    over
  }: DragEndEvent) => {
    setActiveDragKey(null);
    if (!over || active.id === over.id) return;
    setSources(prev => {
      const oldIndex = prev.findIndex(s => s.key === active.id);
      const newIndex = prev.findIndex(s => s.key === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      const next = [...prev];
      const [moved] = next.splice(oldIndex, 1);
      next.splice(newIndex, 0, moved);
      return next;
    });
    setDirty(true);
  };
  const handleAddSource = () => {
    setSources(prev => [...prev, emptyDraft()]);
    setDirty(true);
  };
  const handleRemoveSource = (index: number) => {
    setSources(prev => prev.filter((_, i) => i !== index));
    setDirty(true);
  };
  const handleChangeSource = (index: number, draft: SourceDraft) => {
    setSources(prev => prev.map((s, i) => i === index ? draft : s));
    setDirty(true);
  };
  const handleSave = useCallback(async () => {
    // In auto mode the backend scans all org wallets — no source config needed.
    if (!isAutoMode) {
      if (sources.length === 0) {
        toast.error("Add at least one source before saving.");
        return;
      }
      for (let i = 0; i < sources.length; i++) {
        const s = sources[i];
        if (!s.walletId) {
          toast.error(`Source ${i + 1}: wallet is required.`);
          return;
        }
        if (!s.chainId) {
          toast.error(`Source ${i + 1}: chain is required.`);
          return;
        }
        if (!s.tokenAddress) {
          toast.error(`Source ${i + 1}: token is required.`);
          return;
        }
        const bps = s.slippageBps !== "" ? Number(s.slippageBps) : 300;
        if (Number.isNaN(bps) || bps < 0 || bps > 8000) {
          toast.error(`Source ${i + 1}: slippage must be 0–8000 bps.`);
          return;
        }
      }
    }

    // Always persist sources so they survive mode switches.
    const parsed = sources.map((s, i) => ({
      id: s.id,
      walletId: s.walletId,
      tokenAddress: s.tokenAddress,
      chainId: s.chainId ?? 0,
      tokenDecimals: s.tokenDecimals,
      slippageBps: s.slippageBps !== "" ? Number(s.slippageBps) : 300,
      priority: i
    }));
    const parsedMaxSwap = maxSwapAmountUsd !== "" ? Number(maxSwapAmountUsd) : 3;
    const ok = await save(enabled, mode, parsedMaxSwap, parsed);
    if (ok) {
      toast.success("Auto-gas settings saved.");
      setDirty(false);
      onSaved?.();
    } else {
      toast.error("Failed to save auto-gas settings. Check the values and try again.");
    }
  }, [enabled, mode, maxSwapAmountUsd, sources, save, isAutoMode, onSaved]);
  const walletOptions: TurnkeyWallet[] = (wallets ?? []).map(w => ({
    address: w.address,
    walletId: w.id,
    walletName: w.name
  }));
  const previewWallets = useMemo(() => getAutoSwapCandidateWallets((wallets ?? []) as OrganizationWallet[], settings).map(wallet => ({
    address: wallet.address,
    walletId: wallet.id,
    walletName: wallet.name
  })), [wallets, settings]);
  const {
    walletBalances,
    isLoading: previewBalancesLoading
  } = useAggregatedWalletBalances(previewWallets.map(wallet => ({
    id: wallet.walletId ?? "",
    address: wallet.address,
    name: wallet.walletName
  })), enabled && isAutoMode);
  const activeDraft = sources.find(s => s.key === activeDragKey);
  if (loading || walletsLoading) {
    return <AutoSwapConfigSkeleton />;
  }
  const ownerNotice = !isOwner ? <div className={cn(variant === "page" && "col-span-full", "rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900/40 dark:bg-amber-900/10")}>
      <p className="body-sm text-amber-700 dark:text-amber-300">
        Only organization owners can modify auto-gas settings. You have read-only access.
      </p>
    </div> : null;
  const settingsContent = <div className="space-y-6">
      {/* Auto-gas toggle */}
      <div className="border-ds-border-default flex items-center justify-between gap-6 border-b pb-6">
        <button type="button" onClick={() => handleEnabledChange(!enabled)} disabled={!isOwner} className="flex min-w-0 flex-1 flex-col gap-1 text-left disabled:cursor-not-allowed">
          <p className="body-sm-semibold">Auto-Gas</p>
          <p className="body-sm text-ds-text-muted">Automatically cover gas fees for all workflows in this org.</p>
        </button>
        <Switch checked={enabled} disabled={!isOwner} onCheckedChange={handleEnabledChange} />
      </div>

      {/* Auto-swap info */}
      <div className="border-ds-border-default flex items-center justify-between gap-6 border-b pb-6">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="body-sm-semibold">Auto-Swap</p>
          <p className="body-sm text-ds-text-muted">
            Automatically cover insufficient token balances. Enabled per workflow in the workflow settings.
          </p>
        </div>
        <span className="body-sm text-ds-text-disabled dark:text-ds-text-muted whitespace-nowrap">Per workflow</span>
      </div>

      {enabled && <>
          <div className={cn(isAutoMode && "grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start")}>
            <div>
              <p className="label label-xs mb-2">Source selection mode</p>
              <RadioGroup value={mode} onValueChange={value => handleModeChange(value as AutoSwapMode)} className="divide-ds-neutral-200 dark:divide-ds-neutral-800 mt-2 gap-0 divide-y py-4">
                {([{
              value: "auto_lowest_balance" as AutoSwapMode,
              label: "Lowest USD value first",
              desc: "Drain smallest positions first, combine until covered"
            }, {
              value: "auto_highest_balance" as AutoSwapMode,
              label: "Highest USD value first",
              desc: "Use the largest source first, combine until covered"
            }, {
              value: "manual" as AutoSwapMode,
              label: "Manual",
              desc: "Try sources in your defined priority order"
            }] satisfies {
              value: AutoSwapMode;
              label: string;
              desc: string;
            }[]).map(({
              value,
              label,
              desc
            }) => <label key={value} htmlFor={`auto-swap-mode-${value}`} className="flex cursor-pointer items-center gap-4 py-4 text-left first:pt-0 last:pb-0">
                    <RadioGroupItem value={value} id={`auto-swap-mode-${value}`} className="shrink-0" />
                    <div className="min-w-0">
                      <p className={`body-sm-semibold ${mode === value ? "text-blue-700 dark:text-blue-300" : "text-ds-text-primary"}`}>
                        {label}
                      </p>
                      <p className="body-sm text-ds-text-muted mt-1">{desc}</p>
                    </div>
                  </label>)}
              </RadioGroup>
            </div>

            {isAutoMode ? <AutoSwapModePreviewCard mode={mode} wallets={previewWallets} walletBalances={walletBalances} isLoading={previewBalancesLoading} /> : null}
          </div>

          {!isAutoMode ? <>
              <DndContext id={dndId} sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                <SortableContext items={sources.map(s => s.key)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2">
                    {sources.map((src, i) => <SortableSourceRow key={src.key} draft={src} wallets={walletOptions} index={i} onChange={d => handleChangeSource(i, d)} onRemove={() => handleRemoveSource(i)} />)}
                  </div>
                </SortableContext>

                <DragOverlay>
                  {activeDraft && <div className="bg-ds-surface-card rounded-xl border border-blue-400 shadow-xl dark:border-blue-500">
                      <div className="flex items-center gap-2 px-3 py-2.5">
                        <IconGripDotsVerticalOutline18 className="text-ds-text-disabled h-4 w-4" />
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-xs font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                          {sources.findIndex(s => s.key === activeDragKey) + 1}
                        </span>
                        <span className="ml-1 min-w-0 truncate">
                          <SourceSummary draft={activeDraft} wallets={walletOptions} />
                        </span>
                      </div>
                    </div>}
                </DragOverlay>
              </DndContext>

              {sources.length < 5 && <button type="button" onClick={handleAddSource} className="border-ds-neutral-300 text-ds-text-muted hover:text-ds-text-secondary dark:border-ds-neutral-700 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed py-3 text-sm transition-colors hover:border-zinc-400 dark:hover:border-zinc-500 dark:hover:text-zinc-300">
                  <IconPlusOutline18 className="h-4 w-4" />
                  Add source
                </button>}
            </> : null}
        </>}

      {enabled && <div>
          <button type="button" onClick={() => setShowAdvanced(prev => !prev)} className="label label-xs hover:text-ds-text-primary dark:text-ds-text-disabled flex w-full flex-1 items-center gap-1.5 pt-4 transition-colors dark:hover:text-white">
            <IconChevronDownOutline18 className={`h-4 w-4 transition-transform ${showAdvanced ? "rotate-0" : "-rotate-90"}`} />
            Advanced settings
          </button>
          {showAdvanced && <div className="mt-4">
              <label className="field-label mb-1.5 block">Max auto-gas amount (USD)</label>
              <div className="relative max-w-xs">
                <span className="text-ds-text-disabled pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm">
                  $
                </span>
                <Input type="number" value={maxSwapAmountUsd} onChange={e => {
            setMaxSwapAmountUsd(e.target.value);
            setDirty(true);
          }} disabled={!isOwner} min={0} placeholder="3" className="pl-7" />
              </div>
              <p className="text-ds-text-disabled dark:text-ds-text-muted mt-1.5 text-xs">
                Safety cap per individual swap. Defaults to $3.
              </p>
            </div>}
        </div>}

      <div className="flex justify-end">
        <Button disabled={!isOwner || !dirty || saving} onClick={handleSave} className="px-4">
          {saving ? <span className="flex items-center gap-1.5">
              <svg className="h-3.5 w-3.5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Saving
            </span> : "Save settings"}
        </Button>
      </div>
    </div>;
  if (variant === "modal") {
    return <div className="space-y-6">
        {ownerNotice}
        <div>
          <h2 className="text-ds-text-primary text-xl font-semibold">Automatic Token Swap</h2>
          <p className="text-ds-text-muted mt-1 text-sm">
            Automatically fund gas by swapping from available wallet balances when needed.
          </p>
        </div>
        {settingsContent}
      </div>;
  }
  return <div className="grid gap-8 lg:grid-cols-[280px,1fr] lg:gap-12">
      {ownerNotice}
      <div className="lg:sticky lg:top-8 lg:self-start">
        <h2 className="heading-lg">Auto-Gas</h2>

        <p className="text-ds-text-muted mt-2 text-sm leading-relaxed">
          Automatically swap tokens to cover gas fees across all workflows. For token auto-swap, enable it per workflow
          in the workflow settings.
        </p>
        <AutoSwapTokenAnimation />
      </div>

      {settingsContent}
    </div>;
}
export default function AutoSwapSettingsTab(t0) {
  const $ = _c(2);
  const {
    isOwner: t1
  } = t0;
  const isOwner = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== isOwner) {
    t2 = <AutoSwapSettingsPanel isOwner={isOwner} variant="page" />;
    $[0] = isOwner;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
}